<template>
  <div class="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
    <!-- Header -->
    <header class="bg-white/80 backdrop-blur-md border-b border-gray-200 sticky top-0 z-50">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 py-4">
        <div class="flex items-center justify-between">
          <a href="https://tracetail.io" class="flex items-center space-x-3 hover:opacity-80 transition-opacity">
            <div class="bg-gradient-to-br from-blue-600 to-indigo-600 p-2 rounded-lg">
              <svg class="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 11c0 3.517-1.009 6.799-2.753 9.571m-3.44-2.04l.054-.09A13.916 13.916 0 008 11a4 4 0 118 0c0 1.017-.07 2.019-.203 3m-2.118 6.844A21.88 21.88 0 0015.171 17m3.839 1.132c.645-2.266.99-4.659.99-7.132A8 8 0 008 4.07M3 15.364c.64-1.319 1-2.8 1-4.364 0-1.457.39-2.823 1.07-4" />
              </svg>
            </div>
            <div>
              <h1 class="text-2xl font-bold text-gray-900">
                TraceTail <span class="text-blue-600">Code Examples</span>
              </h1>
              <p class="text-sm text-gray-600">@tracetail/vue package guide</p>
            </div>
          </a>
          <a href="https://tracetail.io" class="text-sm text-blue-600 hover:text-blue-700 font-medium">
            Back to Main Site →
          </a>
        </div>
      </div>
    </header>

    <main class="max-w-7xl mx-auto px-4 sm:px-6 py-12">
      <!-- Hero Section -->
      <div class="text-center mb-12">
        <div class="inline-flex items-center px-4 py-2 bg-green-100 text-green-700 rounded-full mb-4">
          <svg class="w-6 h-6 mr-2" viewBox="0 0 256 221" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMinYMin meet">
            <path d="M204.8 0H256L128 220.8 0 0h97.92L128 51.2 157.44 0h47.36z" fill="#41B883"/>
            <path d="M0 0l128 220.8L256 0h-51.2L128 132.48 50.56 0H0z" fill="#41B883"/>
            <path d="M50.56 0L128 133.12 204.8 0h-47.36L128 51.2 97.92 0H50.56z" fill="#35495E"/>
          </svg>
          <span class="text-sm font-semibold">Vue 3 Live Demo</span>
        </div>
        <h2 class="text-4xl font-bold text-gray-900 mb-4">
          Experience <span class="text-blue-600">99.5% Accuracy</span> Fingerprinting
        </h2>
        <p class="text-xl text-gray-600 max-w-2xl mx-auto">
          See our <code class="px-2 py-1 bg-gray-100 rounded text-sm">@tracetail/vue</code> package
          in action with live browser fingerprinting and visitor identification.
        </p>
      </div>

      <!-- Interactive Demo Card -->
      <div class="bg-white rounded-2xl shadow-xl overflow-hidden mb-8">
        <!-- Status Bar -->
        <div :class="['px-6 py-4 border-b', loading ? 'bg-yellow-50 border-yellow-200' : error ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200']">
          <div class="flex items-center justify-between">
            <div class="flex items-center space-x-3">
              <div v-if="loading" class="animate-spin rounded-full h-5 w-5 border-2 border-yellow-600 border-t-transparent"></div>
              <svg v-else-if="error" class="h-5 w-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <svg v-else class="h-5 w-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
              </svg>
              <span :class="[loading ? 'text-yellow-800' : error ? 'text-red-800' : 'text-green-800', 'font-medium']">
                {{ loading ? 'Generating fingerprint...' : error ? `Error: ${error.message}` : 'Fingerprint generated successfully' }}
              </span>
            </div>
            <button @click="regenerate" :disabled="loading" class="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all">
              <svg :class="['h-4 w-4', loading ? 'animate-spin' : '']" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              <span>{{ loading ? 'Generating...' : 'Regenerate' }}</span>
            </button>
          </div>
        </div>

        <!-- Fingerprint Display -->
        <div v-if="fingerprint && !loading" class="p-8">
          <!-- Visitor ID -->
          <div class="text-center mb-8 pb-8 border-b">
            <div class="inline-flex items-center justify-center p-4 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl mb-4 shadow-lg">
              <svg class="h-12 w-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 11c0 3.517-1.009 6.799-2.753 9.571m-3.44-2.04l.054-.09A13.916 13.916 0 008 11a4 4 0 118 0c0 1.017-.07 2.019-.203 3m-2.118 6.844A21.88 21.88 0 0015.171 17m3.839 1.132c.645-2.266.99-4.659.99-7.132A8 8 0 008 4.07M3 15.364c.64-1.319 1-2.8 1-4.364 0-1.457.39-2.823 1.07-4" />
              </svg>
            </div>
            <h3 class="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-2">Visitor ID</h3>
            <div class="font-mono text-3xl font-bold text-gray-900 break-all px-4">{{ fingerprint.visitorId }}</div>
            <p class="text-sm text-gray-500 mt-2">Unique device identifier - consistent across sessions</p>
          </div>

          <!-- Key Metrics -->
          <div class="grid md:grid-cols-3 gap-6 mb-8">
            <!-- Confidence -->
            <div class="bg-gradient-to-br from-blue-50 to-indigo-50 p-6 rounded-xl border border-blue-200">
              <div class="flex items-center justify-between mb-4">
                <svg class="h-6 w-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span class="text-3xl font-bold text-blue-600">{{ (fingerprint.confidence * 100).toFixed(1) }}%</span>
              </div>
              <div class="text-sm font-semibold text-gray-700 mb-1">Confidence Score</div>
              <div class="text-xs text-gray-500">Identification accuracy</div>
              <div class="mt-3 bg-gray-200 rounded-full h-2 overflow-hidden">
                <div class="bg-gradient-to-r from-blue-500 to-indigo-600 h-full transition-all duration-500" :style="`width: ${fingerprint.confidence * 100}%`"></div>
              </div>
            </div>

            <!-- Processing Time -->
            <div class="bg-gradient-to-br from-green-50 to-emerald-50 p-6 rounded-xl border border-green-200">
              <div class="flex items-center justify-between mb-4">
                <svg class="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                <span class="text-3xl font-bold text-green-600">{{ fingerprint.processingTime }}ms</span>
              </div>
              <div class="text-sm font-semibold text-gray-700 mb-1">Processing Time</div>
              <div class="text-xs text-gray-500">Performance target achieved</div>
              <div class="mt-3 flex items-center text-xs text-green-600">
                <svg class="h-3 w-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                </svg>
                <span>Performance optimized</span>
              </div>
            </div>

            <!-- Signal Sources -->
            <div class="bg-gradient-to-br from-purple-50 to-pink-50 p-6 rounded-xl border border-purple-200">
              <div class="flex items-center justify-between mb-4">
                <svg class="h-6 w-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                <span class="text-3xl font-bold text-purple-600">{{ countAllSignalDataPoints(fingerprint.signals || {}) }}</span>
              </div>
              <div class="text-sm font-semibold text-gray-700 mb-1">Signal Data Points</div>
              <div class="text-xs text-gray-500">Browser characteristics analyzed</div>
              <div class="mt-3 flex items-center text-xs text-purple-600">
                <svg class="h-3 w-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                </svg>
                <span>Multi-vector analysis</span>
              </div>
            </div>
          </div>

          <!-- Browser Signals -->
          <div class="border-t pt-8">
            <h4 class="text-lg font-bold text-gray-900 mb-4 flex items-center">
              <svg class="h-5 w-5 mr-2 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              Detected Browser Signals
            </h4>
            <div class="grid md:grid-cols-2 gap-4">
              <div v-for="(signal, index) in browserSignals" :key="index" class="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                <div class="h-5 w-5 text-gray-400 mt-0.5 flex-shrink-0" v-html="signal.icon"></div>
                <div class="flex-1 min-w-0">
                  <div class="text-sm font-medium text-gray-700">{{ signal.label }}</div>
                  <div class="text-xs text-gray-500 truncate">{{ signal.value }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Package Links -->
      <div class="bg-white rounded-xl shadow-md p-6 mb-8">
        <h3 class="text-lg font-bold text-gray-900 mb-4">SDK Resources</h3>
        <div class="grid md:grid-cols-2 gap-4">
          <a href="https://www.npmjs.com/package/@tracetail/vue" target="_blank" class="flex items-center space-x-3 p-4 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors">
            <div class="w-12 h-12 rounded flex items-center justify-center flex-shrink-0">
              <img src="https://upload.wikimedia.org/wikipedia/commons/d/db/Npm-logo.svg" alt="NPM" class="w-10 h-10" />
            </div>
            <div>
              <div class="text-sm font-semibold text-gray-900">NPM Package</div>
              <div class="text-xs text-gray-600">@tracetail/vue</div>
            </div>
          </a>
          <a href="https://tracetail.io/docs/sdks/vue" target="_blank" class="flex items-center space-x-3 p-4 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors">
            <svg class="h-8 w-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
            </svg>
            <div>
              <div class="text-sm font-semibold text-gray-900">Documentation</div>
              <div class="text-xs text-gray-600">Full API reference</div>
            </div>
          </a>
        </div>
      </div>

      <!-- Installation -->
      <div class="mb-8">
        <CodeBlock
          title="Installation"
          language="bash"
          :code="installCode"
        />
      </div>

      <!-- Composition API Example -->
      <div class="mt-8">
        <h3 class="text-2xl font-bold text-gray-900 mb-4">Composition API (Recommended)</h3>
        <p class="text-gray-600 mb-4">
          The Composition API provides a flexible way to use TraceTail with Vue 3's script setup syntax.
        </p>
        <CodeBlock
          title="useTraceTail Composable"
          language="typescript"
          :code="compositionExample"
        />
      </div>

      <!-- Options API Example -->
      <div class="mt-8">
        <h3 class="text-2xl font-bold text-gray-900 mb-4">Options API</h3>
        <p class="text-gray-600 mb-4">
          For projects using the Options API, TraceTail can be integrated through lifecycle hooks.
        </p>
        <CodeBlock
          title="Options API Component"
          language="typescript"
          :code="optionsExample"
        />
      </div>

      <!-- Configuration Options -->
      <div class="mt-8">
        <h3 class="text-2xl font-bold text-gray-900 mb-4">Configuration Options</h3>
        <div class="bg-white rounded-xl shadow-md p-6">
          <div class="overflow-x-auto">
            <table class="min-w-full">
              <thead>
                <tr class="border-b">
                  <th class="text-left py-3 px-4 font-semibold text-gray-900">Option</th>
                  <th class="text-left py-3 px-4 font-semibold text-gray-900">Type</th>
                  <th class="text-left py-3 px-4 font-semibold text-gray-900">Default</th>
                  <th class="text-left py-3 px-4 font-semibold text-gray-900">Description</th>
                </tr>
              </thead>
              <tbody>
                <tr class="border-b" v-for="option in configOptions" :key="option.name">
                  <td class="py-3 px-4"><code class="text-sm bg-gray-100 px-2 py-1 rounded">{{ option.name }}</code></td>
                  <td class="py-3 px-4 text-sm text-gray-600">{{ option.type }}</td>
                  <td class="py-3 px-4 text-sm text-gray-600">{{ option.default }}</td>
                  <td class="py-3 px-4 text-sm text-gray-600">{{ option.description }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Advanced Usage -->
      <div class="mt-8">
        <h3 class="text-2xl font-bold text-gray-900 mb-4">Advanced Usage</h3>
        <p class="text-gray-600 mb-4">
          Access detailed fingerprint components for custom analysis and processing.
        </p>
        <CodeBlock
          title="Verbose Mode Example"
          language="typescript"
          :code="advancedExample"
        />
      </div>

      <!-- Other Demo Links -->
      <div class="mt-8 bg-white rounded-xl shadow-md p-6">
        <h3 class="text-lg font-bold text-gray-900 mb-4">Explore Other Framework Demos</h3>
        <div class="grid md:grid-cols-3 gap-4">
          <a href="/" class="p-4 border border-gray-200 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-all">
            <div class="mb-2 flex justify-start">
              <svg class="w-8 h-8" viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg">
                <path d="M418.2 177.2c-5.4-1.8-10.8-3.5-16.2-5.1.9-3.7 1.7-7.4 2.5-11.1 12.3-59.6 4.2-107.5-23.1-123.3-26.3-15.1-69.2.6-112.6 38.4-4.3 3.7-8.5 7.6-12.5 11.5-2.7-2.6-5.5-5.2-8.3-7.7-45.5-40.4-91.1-57.4-118.4-41.5-26.2 15.2-34 60.3-23 116.7 1.1 5.6 2.3 11.1 3.7 16.7-6.4 1.8-12.7 3.8-18.6 5.9C38.3 196.2 0 225.4 0 255.6c0 31.2 40.8 62.5 96.3 81.5 4.5 1.5 9 3 13.6 4.3-1.5 6-2.8 11.9-4 18-10.5 55.5-2.3 99.5 23.9 114.6 27 15.6 72.4-.4 116.6-39.1 3.5-3.1 7-6.3 10.5-9.7 4.4 4.3 9 8.4 13.6 12.4 42.8 36.8 85.1 51.7 111.2 36.6 27-15.6 35.8-62.9 24.4-120.5-.9-4.4-1.9-8.9-3-13.5 3.2-.9 6.3-1.9 9.4-2.9 57.7-19.1 99.5-50 99.5-81.7 0-30.3-39.4-59.7-93.8-78.4zM282.9 92.3c37.2-32.4 71.9-45.1 87.7-36 16.9 9.7 23.4 48.9 12.8 100.4-.7 3.4-1.4 6.7-2.3 10-22.2-5-44.7-8.6-67.3-10.6-13-18.6-27.2-36.4-42.6-53.1 3.9-3.7 7.7-7.2 11.7-10.7zM167.2 307.5c5.1 8.7 10.3 17.4 15.8 25.9-15.6-1.7-31.1-4.2-46.4-7.5 4.4-14.4 9.9-29.3 16.3-44.5 4.6 8.8 9.3 17.5 14.3 26.1zm-30.3-120.3c14.4-3.2 29.7-5.8 45.6-7.8-5.3 8.3-10.5 16.8-15.4 25.4-4.9 8.5-9.7 17.2-14.2 26-6.3-14.9-11.6-29.5-16-43.6zm27.4 68.9c6.6-13.8 13.8-27.3 21.4-40.6s15.8-26.2 24.4-38.9c15-1.1 30.3-1.7 45.9-1.7s31 .6 45.9 1.7c8.5 12.6 16.6 25.5 24.3 38.7s14.9 26.7 21.7 40.4c-6.7 13.8-13.9 27.4-21.6 40.8-7.6 13.3-15.7 26.2-24.2 39-14.9 1.1-30.4 1.6-46.1 1.6s-30.9-.5-45.6-1.4c-8.7-12.7-16.9-25.7-24.6-39s-14.8-26.8-21.5-40.6zm180.6 51.2c5.1-8.8 9.9-17.7 14.6-26.7 6.4 14.5 12 29.2 16.9 44.3-15.5 3.5-31.2 6.2-47 8 5.4-8.4 10.5-17 15.5-25.6zm14.4-76.5c-4.7-8.8-9.5-17.6-14.5-26.2-4.9-8.5-10-16.9-15.3-25.2 16.1 2 31.5 4.7 45.9 8-4.6 14.8-10 29.2-16.1 43.4zM256.2 118.3c10.5 11.4 20.4 23.4 29.6 35.8-19.8-.9-39.7-.9-59.5 0 9.8-12.9 19.9-24.9 29.9-35.8zM140.2 57c16.8-9.8 54.1 4.2 93.4 39 2.5 2.2 5 4.6 7.6 7-15.5 16.7-29.8 34.5-42.9 53.1-22.6 2-45 5.5-67.2 10.4-1.3-5.1-2.4-10.3-3.5-15.5-9.4-48.4-3.2-84.9 12.6-94zm-24.5 263.6c-4.2-1.2-8.3-2.5-12.4-3.9-21.3-6.7-45.5-17.3-63-31.2-10.1-7-16.9-17.8-18.8-29.9 0-18.3 31.6-41.7 77.2-57.6 5.7-2 11.5-3.8 17.3-5.5 6.8 21.7 15 43 24.5 63.6-9.6 20.9-17.9 42.5-24.8 64.5zm116.6 98c-16.5 15.1-35.6 27.1-56.4 35.3-11.1 3.9-23.9 4.1-35.3.4-17.2-9.8-24.4-47.8-14.6-96.3 1-5 2.1-10 3.4-15 22.1 4.8 45.7 8.1 70.8 9.9 13 18.7 27.3 36.6 42.8 53.6-3.2 3.1-6.4 6.1-9.6 8.9zm24.5-24.3c-10.2-11-20.4-23.2-30.3-36.3 9.6.4 19.5.6 29.5.6 10.3 0 20.4-.2 30.4-.7-9.2 12.7-19.1 24.8-29.6 36.4zm130.7 30c-.9 12.2-6.9 23.6-16.5 31.3-15.9 9.2-49.8-2.8-86.4-34.2-4.2-3.6-8.4-7.5-12.7-11.5 15.3-16.9 29.4-34.8 42.2-53.6 22.9-1.9 45.7-5.4 68.2-10.5 1 4.1 1.9 8.2 2.7 12.2 4.9 21.6 5.7 44.1 2.5 66.3zm18.2-107.5c-2.8.9-5.6 1.8-8.5 2.6-7-21.8-15.6-43.1-25.5-63.8 9.6-20.4 17.7-41.4 24.5-62.9 5.2 1.5 10.2 3.1 15 4.7 46.6 16 79.3 39.8 79.3 58 0 19.6-34.9 44.9-84.8 61.4zm-149.7-15c25.3 0 45.8-20.5 45.8-45.8s-20.5-45.8-45.8-45.8c-25.3 0-45.8 20.5-45.8 45.8s20.5 45.8 45.8 45.8z" fill="#61DAFB"/>
              </svg>
            </div>
            <div class="text-sm font-semibold text-gray-900">React Demo</div>
            <div class="text-xs text-gray-600">Live interactive demo</div>
          </a>
          <a href="/js" class="p-4 border border-gray-200 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-all">
            <div class="mb-2 flex justify-start">
              <svg class="w-8 h-8" viewBox="0 0 256 256" xmlns="http://www.w3.org/2000/svg">
                <path d="M0 0h256v256H0V0z" fill="#F7DF1E"/>
                <path d="M67.312 213.932l19.59-11.856c3.78 6.701 7.218 12.371 15.465 12.371 7.905 0 12.89-3.092 12.89-15.12v-81.798h24.057v82.138c0 24.917-14.606 36.259-35.916 36.259-19.245 0-30.416-9.967-36.087-21.996M152.381 211.354l19.588-11.341c5.157 8.421 11.859 14.607 23.715 14.607 9.969 0 16.325-4.984 16.325-11.858 0-8.248-6.53-11.17-17.528-15.98l-6.013-2.58c-17.357-7.387-28.87-16.667-28.87-36.257 0-18.044 13.747-31.792 35.228-31.792 15.294 0 26.292 5.328 34.196 19.247L210.29 147.43c-4.125-7.389-8.591-10.31-15.465-10.31-7.046 0-11.514 4.468-11.514 10.31 0 7.217 4.468 10.14 14.778 14.608l6.014 2.577c20.45 8.765 31.963 17.7 31.963 37.804 0 21.654-17.012 33.51-39.867 33.51-22.339 0-36.774-10.654-43.819-24.574" fill="#000"/>
              </svg>
            </div>
            <div class="text-sm font-semibold text-gray-900">JavaScript Demo</div>
            <div class="text-xs text-gray-600">Vanilla JS implementation</div>
          </a>
          <a href="/angular" class="p-4 border border-gray-200 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-all">
            <div class="mb-2 flex justify-start">
              <svg class="w-8 h-8" viewBox="0 0 250 250" xmlns="http://www.w3.org/2000/svg">
                <polygon points="125,30 125,30 125,30 31.9,63.2 46.1,186.3 125,230 125,230 125,230 203.9,186.3 218.1,63.2" fill="#DD0031"/>
                <polygon points="125,30 125,52.2 125,52.1 125,153.4 125,153.4 125,230 125,230 203.9,186.3 218.1,63.2 125,30" fill="#C3002F"/>
                <path d="M125,52.1L66.8,182.6h0h21.7h0l11.7-29.2h49.4l11.7,29.2h0h21.7h0L125,52.1L125,52.1L125,52.1L125,52.1 L125,52.1z M142,135.4H108l17-40.9L142,135.4z" fill="#FFFFFF"/>
              </svg>
            </div>
            <div class="text-sm font-semibold text-gray-900">Angular Demo</div>
            <div class="text-xs text-gray-600">Angular with TypeScript</div>
          </a>
        </div>
      </div>

      <!-- Footer CTA -->
      <div class="mt-12 text-center">
        <div class="inline-flex flex-col items-center space-y-4 p-8 bg-white rounded-2xl shadow-lg">
          <h3 class="text-2xl font-bold text-gray-900">Ready to get started?</h3>
          <p class="text-gray-600 max-w-md">
            Install <code class="px-2 py-1 bg-gray-100 rounded">@tracetail/vue</code> and
            start protecting your Vue application today.
          </p>
          <div class="flex flex-col sm:flex-row gap-4">
            <a href="https://tracetail.io/get-started" class="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold transition-all">
              Get API Key
            </a>
            <a href="https://www.npmjs.com/package/@tracetail/vue" class="px-6 py-3 bg-gray-100 text-gray-900 rounded-lg hover:bg-gray-200 font-semibold transition-all">
              View on NPM
            </a>
          </div>
        </div>
      </div>
    </main>

    <!-- Footer -->
    <footer class="mt-16 border-t border-gray-200 bg-white/50">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div class="flex flex-col items-center space-y-6">
          <div class="flex items-center space-x-2">
            <div class="bg-gradient-to-br from-blue-600 to-indigo-600 p-2 rounded-lg">
              <svg class="h-5 w-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 11c0 3.517-1.009 6.799-2.753 9.571m-3.44-2.04l.054-.09A13.916 13.916 0 008 11a4 4 0 118 0c0 1.017-.07 2.019-.203 3m-2.118 6.844A21.88 21.88 0 0015.171 17m3.839 1.132c.645-2.266.99-4.659.99-7.132A8 8 0 008 4.07M3 15.364c.64-1.319 1-2.8 1-4.364 0-1.457.39-2.823 1.07-4" />
              </svg>
            </div>
            <span class="text-xl font-bold text-gray-900">TraceTail</span>
          </div>
          <div class="text-center">
            <p class="text-sm text-gray-600 mb-3">
              Enterprise Browser Fingerprinting with over 99.5% Accuracy
            </p>
            <div class="flex flex-wrap items-center justify-center gap-4 text-sm">
              <a href="https://tracetail.io/docs" class="text-gray-500 hover:text-gray-700 transition-colors">Documentation</a>
              <span class="text-gray-300">•</span>
              <a href="https://tracetail.io/blog" class="text-gray-500 hover:text-gray-700 transition-colors">Blog</a>
            </div>
          </div>
          <div class="text-center text-xs text-gray-400">
            <p>© 2025 TraceTail. All rights reserved.</p>
          </div>
        </div>
      </div>
    </footer>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useFingerprint } from '@tracetail/vue';
import CodeBlock from './components/CodeBlock.vue';

// Count all individual data points in components (not just top-level categories)
function countAllSignalDataPoints(obj, visited = new WeakSet()) {
  if (!obj || typeof obj !== 'object') return 0;

  // Prevent infinite recursion
  if (visited.has(obj)) return 0;
  visited.add(obj);

  // Arrays: count each element individually (e.g., array of 16 fonts = 16 data points)
  if (Array.isArray(obj)) {
    return obj.reduce((sum, item) => {
      if (typeof item === 'object' && item !== null) {
        return sum + countAllSignalDataPoints(item, visited);
      }
      return sum + 1; // Each primitive in array counts as 1
    }, 0);
  }

  // Objects: count all leaf properties recursively
  let count = 0;
  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      const value = obj[key];
      if (value === null || value === undefined) {
        continue; // Skip null/undefined
      } else if (typeof value === 'object') {
        count += countAllSignalDataPoints(value, visited);
      } else {
        count += 1; // Primitive value = 1 data point
      }
    }
  }

  return count;
}

// Use the real TraceTail fingerprint composable
const {
  visitorId,
  confidence,
  riskScore,
  fraudulent,
  signals,
  processingTime,
  loading,
  error,
  refresh
} = useFingerprint();

// Create a computed fingerprint object for compatibility with template
const fingerprint = computed(() => {
  if (!visitorId.value) return null;
  return {
    visitorId: visitorId.value,
    confidence: confidence.value,
    riskScore: riskScore.value,
    fraudulent: fraudulent.value,
    signals: signals.value,
    processingTime: processingTime.value,
    components: signals.value || {} // Pass all signals instead of filtering to just 4
  };
});

// Regenerate function using the composable's refresh method
const regenerate = async () => {
  await refresh();
};

// Browser signals for display
const browserSignals = computed(() => [
  {
    icon: '<svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>',
    label: 'Screen Resolution',
    value: `${screen.width}x${screen.height}`
  },
  {
    icon: '<svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>',
    label: 'User Agent',
    value: 'Detected'
  },
  {
    icon: '<svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" /></svg>',
    label: 'Canvas Fingerprint',
    value: 'Generated'
  },
  {
    icon: '<svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>',
    label: 'WebGL Renderer',
    value: 'Detected'
  },
  {
    icon: '<svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>',
    label: 'Timezone',
    value: Intl.DateTimeFormat().resolvedOptions().timeZone
  },
  {
    icon: '<svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" /></svg>',
    label: 'Platform',
    value: navigator.platform
  }
]);

const installCode = `npm install @tracetail/vue
# or
yarn add @tracetail/vue
# or
pnpm add @tracetail/vue`;

const compositionExample = `<script setup>
import { onMounted } from 'vue';
import { useFingerprint } from '@tracetail/vue';

// Initialize fingerprint tracking
const {
  visitorId,
  confidence,
  loading,
  error,
  refresh
} = useFingerprint();

// Auto-generate on mount
onMounted(async () => {
  await refresh();
});
<\/script>

<template>
  <div>
    <div v-if="loading">Generating fingerprint...</div>
    <div v-else-if="error">Error: {{ error.message }}</div>
    <div v-else-if="visitorId">
      <h2>Visitor Identified</h2>
      <p>Visitor ID: {{ visitorId }}</p>
      <p>Confidence: {{ (confidence * 100).toFixed(1) }}%</p>
      <button @click="refresh">Regenerate</button>
    </div>
  </div>
</template>`;

const optionsExample = `<script>
import { TraceTail } from '@tracetail/vue';

export default {
  name: 'FingerprintComponent',
  data() {
    return {
      tracetail: null,
      fingerprint: null,
      loading: false,
      error: null
    };
  },
  mounted() {
    this.initializeTraceTail();
  },
  methods: {
    async initializeTraceTail() {
      this.tracetail = new TraceTail({
        apiKey: 'your-api-key',
        caching: true
      });
      await this.generateFingerprint();
    },
    async generateFingerprint() {
      this.loading = true;
      this.error = null;
      try {
        this.fingerprint = await this.tracetail.generateFingerprint();
      } catch (err) {
        this.error = err;
      } finally {
        this.loading = false;
      }
    }
  }
};
<\/script>

<template>
  <div>
    <div v-if="loading">Generating fingerprint...</div>
    <div v-else-if="error">Error: {{ error.message }}</div>
    <div v-else-if="fingerprint">
      <p>Visitor ID: {{ fingerprint.visitorId }}</p>
      <button @click="generateFingerprint">Regenerate</button>
    </div>
  </div>
</template>`;

const advancedExample = `<script setup>
import { useFingerprint } from '@tracetail/vue';
import { computed, onMounted } from 'vue';

const {
  visitorId,
  signals,
  loading,
  error,
  refresh
} = useFingerprint();

// Access individual fingerprint components
const canvas = computed(() => signals.value?.canvas);
const webgl = computed(() => signals.value?.webgl);
const fonts = computed(() => signals.value?.fonts);

// Auto-generate on mount
onMounted(async () => {
  await refresh();
});
<\/script>

<template>
  <div v-if="!loading && visitorId">
    <h3>Detailed Fingerprint Analysis</h3>
    <div class="component-grid">
      <div class="component">
        <h4>Canvas Fingerprint</h4>
        <p>{{ canvas }}</p>
      </div>
      <div class="component">
        <h4>WebGL Renderer</h4>
        <p>{{ webgl }}</p>
      </div>
      <div class="component">
        <h4>Fonts Detected</h4>
        <p>{{ fonts?.length || 0 }} fonts</p>
      </div>
    </div>
  </div>
</template>`;

const configOptions = [
  {
    name: 'apiKey',
    type: 'string',
    default: 'required',
    description: 'Your TraceTail API key from the dashboard'
  },
  {
    name: 'immediate',
    type: 'boolean',
    default: 'false',
    description: 'Generate fingerprint immediately on mount'
  },
  {
    name: 'caching',
    type: 'boolean',
    default: 'true',
    description: 'Cache fingerprint to reduce computation'
  },
  {
    name: 'verbose',
    type: 'boolean',
    default: 'false',
    description: 'Include detailed component breakdown'
  },
  {
    name: 'debug',
    type: 'boolean',
    default: 'false',
    description: 'Enable debug logging to console'
  }
];
</script>
